/**
 * How a tenant's students give their answers — per kind of attempt, because an
 * institute treats an assignment and a test differently (RG Academy: tests on
 * an OMR sheet, assignments none; DS Academy: tests from the scanner's export).
 *
 * Read in code on every upload instead of a model working it out per upload:
 * an expected OMR is asked for on the page grid, and a required one holds the
 * reading until a page is marked. See answer-sheet-reader-target.md §4a.
 *
 * The editor of the `answerSheets` tenant setting, framed by TenantSettingsTab.
 * The setting is overridden whole: saving sets both modalities for this tenant.
 */
import { useState } from 'react';
import { Button, FormField, Select } from '@shared-ui/components';
import type { AnswerSheetExpectation, TenantAnswerSheets } from '../../../api/tenantsApi';
import type { SettingEditorProps } from './TenantSettingsTab';

type Modality = 'practice' | 'test';

const MODALITIES: Array<{ key: Modality; label: string }> = [
    { key: 'practice', label: 'Assignments' },
    { key: 'test', label: 'Tests' },
];

const SOURCES: Array<{ value: AnswerSheetExpectation['student']; label: string }> = [
    { value: 'none', label: 'No answer sheet' },
    { value: 'omr', label: 'OMR sheet, uploaded with the pages' },
    { value: 'list', label: 'Written answer list' },
    { value: 'export', label: "Scanner's spreadsheet export, uploaded by a teacher" },
];

const NONE: AnswerSheetExpectation = { student: 'none', required: false };

function same(a: TenantAnswerSheets, b: TenantAnswerSheets): boolean {
    return MODALITIES.every(({ key }) => {
        const x = a.byModality[key] ?? NONE;
        const y = b.byModality[key] ?? NONE;
        return x.student === y.student && x.required === y.required;
    });
}

export function AnswerSheetsEditor({ value, save, saving, actions }: SettingEditorProps<'answerSheets'>) {
    const [draft, setDraft] = useState<TenantAnswerSheets>({ byModality: { ...value.byModality } });

    const set = (key: Modality, next: Partial<AnswerSheetExpectation>) =>
        setDraft(d => {
            const merged = { ...(d.byModality[key] ?? NONE), ...next };
            // "None" can't be required — there is nothing to ask for.
            if (merged.student === 'none') merged.required = false;
            return { byModality: { ...d.byModality, [key]: merged } };
        });

    const dirty = !same(value, draft);

    return (
        <div className="tenants-form">
            {MODALITIES.map(({ key, label }) => {
                const current = draft.byModality[key] ?? NONE;
                return (
                    <FormField key={key} label={label}>
                        <Select
                            value={current.student}
                            onChange={e => set(key, { student: e.target.value as AnswerSheetExpectation['student'] })}
                        >
                            {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </Select>
                        <label className="tenant-answer-sheet-required">
                            <input
                                type="checkbox"
                                checked={current.required}
                                disabled={current.student === 'none'}
                                onChange={e => set(key, { required: e.target.checked })}
                            />
                            <span>
                                Required — {current.student === 'omr'
                                    ? 'an upload without a page marked as the OMR sheet is held until the uploader marks one or says there is none'
                                    : 'a missing one is reported on the attempt'}
                            </span>
                        </label>
                    </FormField>
                );
            })}
            <div className="tenant-detail-save">
                {dirty && <span className="tenant-detail-note">Unsaved changes</span>}
                {actions}
                <Button onClick={() => save(draft)} loading={saving} disabled={!dirty}>Save</Button>
            </div>
        </div>
    );
}
